import { ApplicationConfig, LOCALE_ID, provideBrowserGlobalErrorListeners, provideZonelessChangeDetection, importProvidersFrom } from '@angular/core';
import { provideRouter, Router } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideHttpClient, withInterceptors } from '@angular/common/http';

import { routes } from './app.routes';
import { MSAL_INSTANCE, MsalService } from '@azure/msal-angular';
import { AuthService } from './service/auth/auth.service';
import { IPublicClientApplication, PublicClientApplication } from '@azure/msal-browser';
import { environment } from '../environments/environment';
import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { provideToastr } from 'ngx-toastr';
import { MatIconModule } from '@angular/material/icon';
import { provideIcons } from './shared/icons/provide-icons';

// Função para limpar storage, cache e deslogar usuário
const clearStorageAndLogout = async (router?: Router) => {
  try {
    
    // Limpa localStorage e sessionStorage
    sessionStorage.clear();
    localStorage.clear();
    
    // Limpa cache do navegador
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
    }
    
    // Redireciona para a página inicial
    if (router) {
      router.navigate(['/']);
    }
    
  } catch (error) {
    console.error('❌ Erro ao limpar storage e cache:', error);
  }
};

// Função interceptor funcional para Angular 17+
export const authInterceptorFn: HttpInterceptorFn = (req, next) => {
  
  const authService = inject(AuthService);
  const router = inject(Router);

  // URLs que não precisam de autenticação
  const urlsWithoutAuth = [
    environment.apiUrl + '/auth',
    environment.apiUrl + '/auth/azureAD',
    environment.apiUrl + '/auth/refresh',
    environment.apiUrl + '/health',
    environment.apiUrl + '/status'
  ];

  // Verifica se a URL precisa de autenticação
  const shouldSkipAuth = urlsWithoutAuth.some(excludedUrl => req.url.includes(excludedUrl)) ||
                        (!req.url.includes(environment.apiUrl) && !req.url.includes('s3.amazonaws.com'));

  if (shouldSkipAuth) {
    return next(req);
  }

  const token = authService.getAuthorizationToken();

  // Valida se o token existe e não está vazio
  if (!token || token.trim() === '') {
    // Limpa storage - o redirecionamento será feito pelos guards
    clearStorageAndLogout(router);
    return next(req);
  }

  // Adiciona o token à requisição
  const authRequest = req.clone({
    headers: req.headers.set('Authorization', `Bearer ${token}`)
  });


  return next(authRequest).pipe(
    catchError((error: HttpErrorResponse) => {
      console.log('🚨 Erro HTTP detectado:', {
        status: error.status,
        statusText: error.statusText,
        url: error.url,
        message: error.message
      });
      
      // Trata erros de status 401 - desloga o usuário e redireciona
      if (error.status === 401 ) {
        clearStorageAndLogout(router);
      }
      
      // Re-lança o erro para que outros interceptors ou componentes possam tratá-lo
      return throwError(() => error);
    })
  );
};

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.client,
      redirectUri: environment.redirectUri,
      authority: environment.authority,
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: false,
    },
    system: {
      loggerOptions: {
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) {
            return;
          }
          switch (level) {
            case 0: // LogLevel.Error
              return;
            case 1: // LogLevel.Warning
              return;
            case 2: // LogLevel.Info
              return;
            case 3: // LogLevel.Verbose
              return;
          }
        },
        piiLoggingEnabled: false,
        logLevel: 2, // LogLevel.Info
      },
    },
  });
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(routes),
    provideAnimationsAsync(),
    provideToastr(),
    importProvidersFrom(MatIconModule),
    provideHttpClient(withInterceptors([authInterceptorFn])),
    { provide: LOCALE_ID, useValue: 'pt' },
    ...provideIcons(),
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    MsalService,
    AuthService
  ]
};
